import { parse } from 'date-fns';
import { differenceInYears } from 'date-fns/differenceInYears';
import { isAfter } from 'date-fns/isAfter';
import { isValid } from 'date-fns/isValid';
import { ru } from 'date-fns/locale/ru';
import * as Yup from 'yup';
import { string } from 'yup';

import { AGE_MAX, AGE_MIN } from '../components/CreateReview/constants/common';

import { ERROR_DICTIONARY, ERRORS, makeMaxAgeMessage, makeMinAgeMessage } from './errorDictionary';
import { sanitize } from './sanitize';
import { handTypograf, typograf } from './typograf';
import { DEF_CODES, EMAIL_REGEX, ONLY_MOBILE_DEF_CODES } from './validation';

Yup.addMethod(Yup.string, 'normalizePhone', function normalizePhone() {
  return this.transform((_value, originalValue) => {
    if (typeof originalValue === 'string') {
      return originalValue.replace(/[-_()\s]/g, '');
    }
    return originalValue;
  });
});

Yup.addMethod(Yup.string, 'phone', function validatePhone(message) {
  return this.test('phone', message, function (value) {
    if (!value) return true;

    const { path, createError } = this;

    const defCode = /^(8|\+7|7)\(?(\d{3})\)?[\d]{7,10}$/g.exec(value);

    return (defCode !== null && DEF_CODES.indexOf(parseInt(defCode[2], 10)) !== -1) || createError({ path, message });
  });
});

Yup.addMethod(Yup.string, 'mobilePhone', function validatePhone(message) {
  return this.test('phone', message, function (value) {
    if (!value) return true;

    const { path, createError } = this;

    const defCode = /^(8|\+7|7)\(?(\d{3})\)?[\d]{7,10}$/g.exec(value);

    return (
      (defCode && ONLY_MOBILE_DEF_CODES.indexOf(parseInt(defCode[2], 10)) !== -1) || createError({ path, message })
    );
  });
});

Yup.addMethod(Yup.string, 'email', function validateEmail(message) {
  return this.matches(EMAIL_REGEX, {
    message,
    name: 'email',
    excludeEmptyString: true,
  });
});

Yup.addMethod(Yup.string, 'numeric', function validateNumeric(message = ERROR_DICTIONARY[ERRORS.MUST_BE_A_NUMBER]) {
  return this.matches(/^\d+$/, {
    message,
    name: 'numeric',
    excludeEmptyString: true,
  });
});

Yup.addMethod(Yup.string, 'name', function validateName(message = ERROR_DICTIONARY[ERRORS.INVALID_NAME]) {
  return this.matches(/^[-A-ZА-ЯЁ ]+$/i, {
    message,
    name: 'name',
    excludeEmptyString: true,
  });
});

export const dateFormat = 'dd.MM.yyyy';

Yup.addMethod(Yup.string, 'normalizeBirthday', function normalizePhone() {
  return this.transform((_value, originalValue) => {
    if (typeof originalValue === 'string') {
      return originalValue === '__.__.____' ? '' : originalValue;
    }
    return originalValue;
  });
});

Yup.addMethod(Yup.string, 'birthday', function validateBirthday() {
  return this.test('is-valid-date', ERROR_DICTIONARY[ERRORS.INVALID_DATE], (value) => {
    if (!value) return true;

    const parsedDate = parse(value, dateFormat, new Date(), { locale: ru });

    return isValid(parsedDate);
  })
    .test(`is-over-${AGE_MIN}`, makeMinAgeMessage(AGE_MIN), (value) => {
      if (!value) return true;

      const parsedDate = parse(value, dateFormat, new Date(), { locale: ru });
      if (!isValid(parsedDate)) return false;

      const dateNow = new Date();

      const isNowAfterUserDate = isAfter(parsedDate, dateNow);

      if (isNowAfterUserDate) {
        return false;
      }

      return differenceInYears(dateNow, parsedDate) >= AGE_MIN;
    })
    .test(`is-under-${AGE_MAX}`, makeMaxAgeMessage(AGE_MAX), (value) => {
      if (!value) return true;

      const parsedDate = parse(value, dateFormat, new Date(), { locale: ru });
      if (!isValid(parsedDate)) return false;

      const dateNow = new Date();

      const isNowAfterUserDate = isAfter(parsedDate, dateNow);

      if (isNowAfterUserDate) {
        return false;
      }

      return differenceInYears(dateNow, parsedDate) <= AGE_MAX;
    });
});

export const sanitizeAndTypograf = (_value, originalValue) => {
  if (typeof originalValue === 'string') {
    const formattedValue = typograf.execute(handTypograf(originalValue));

    return sanitize(formattedValue);
  }

  return originalValue;
};

Yup.addMethod(Yup.string, 'sanitizeAndTypograf', function transformSanitizeAndTypograf() {
  return this.transform(sanitizeAndTypograf);
});

Yup.addMethod(
  string,
  'repeatingChars',
  function repeatingChars(message: string = ERROR_DICTIONARY[ERRORS.REPEATING_CHARS]) {
    return this.test('repeating-chars', message, function (value: string) {
      const regExp = /([^0-9])\1{3,}/g; // any non-number character
      const repeated = value.toLowerCase().match(regExp);

      return !repeated || this.createError({ message });
    });
  },
);

export default Yup;
